import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FaHeadset, FaFileExcel, FaMagnifyingGlass, FaCircleInfo, FaCircleQuestion } from 'react-icons/fa6';
import axios from '../../configApi/axiosConfig';
import { APIURL } from '../../configApi/apiConfig';
import { AyudaReporteCallCenter } from './AyudaReporteCallCenter';

const TRAMOS = ['1 A 30', '31 A 60', '61 A 99', '100 A 180', 'COSECHAS', 'SIN TRAMO'];
const ESTADOS = ['Cumple meta', 'Sobre el mínimo', 'Bajo el mínimo', 'Sin meta'];

const CLASE_ESTADO = {
    'Cumple meta': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800/50',
    'Sobre el mínimo': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/50',
    'Bajo el mínimo': 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800/60',
    'Sin meta': 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
};

const hoy = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD en hora local
const inicioMes = () => `${hoy().slice(0, 8)}01`;
/** Último día del mes de una fecha YYYY-MM-DD. */
const finDeMes = (fecha) => {
    const [y, m] = fecha.split('-').map(Number);
    return new Date(y, m, 0).toLocaleDateString('en-CA');
};
const mismoMes = (a, b) => a.slice(0, 7) === b.slice(0, 7);

const FILTROS_INICIALES = {
    desde: inicioMes(), hasta: hoy(), tramo: '', idIngresoCobrador: '', extension: '',
    idSupervisor: '', estado: '', tipoDia: '', soloContestadas: false,
};

const soloFecha = (f) => String(f || '').split('T')[0];
const fechaCorta = (f) => {
    const [, m, d] = soloFecha(f).split('-');
    return d && m ? `${d}/${m}` : '';
};

/** Color de la celda de un día: meta cumplida / sobre el mínimo / bajo el mínimo. */
const claseDia = (d) => {
    if (!d) return 'text-slate-300 dark:text-slate-600';
    if (d.MetaSeg == null) return 'text-slate-700 dark:text-slate-300';
    if (d.CumpleDia) return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300';
    if (d.MinimoSeg != null && d.VozSeg >= d.MinimoSeg) return 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    return 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300';
};

const inputCls = 'w-full py-1.5 px-2.5 text-xs rounded-lg bg-white border border-slate-300 text-slate-800 dark:bg-[#161F30] dark:border-slate-700 dark:text-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500';

function Campo({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">{label}</span>
            {children}
        </label>
    );
}

const exportarExcel = ({ resumen, detalle, totales }, filtros) => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumen.map((r) => ({
        Tramo: r.Tramo, Persona: r.Persona, Extension: r.Extension, 'Días trabajados': r.DiasTrabajados,
        Llamadas: r.Llamadas, Contestadas: r.Contestadas, 'Total voz': r.TotalVoz, 'Promedio diario': r.PromedioDiario,
        Mínimo: r.Minimo || '', Meta: r.Meta || '', Estado: r.Estado, '% meta': r.PctMeta ?? '',
        'No contestan': r.NoContestan, Buzón: r.Buzon, 'Buzón (tiempo)': r.BuzonVoz, 'Cortas <10s': r.Cortas,
        'Cortas (tiempo)': r.CortasVoz, 'Sin clasificar': r.SinClasificar, 'Voz efectiva': r.VozEfectiva,
        'Tiempo muerto': r.TiempoMuerto, '% efectividad': r.PctEfectividad ?? '',
    }))), 'Resumen');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detalle.map((d) => ({
        Fecha: soloFecha(d.Fecha), Día: d.DiaSemana, Tramo: d.Tramo, Persona: d.Persona, Extension: d.Extension,
        Llamadas: d.Llamadas, Contestadas: d.Contestadas, Voz: d.Voz, 'Voz (seg)': d.VozSeg,
        'Mínimo (seg)': d.MinimoSeg ?? '', 'Meta (seg)': d.MetaSeg ?? '', 'Cumple día': d.CumpleDia ? 'SI' : 'NO',
        'No contestan': d.NoContestan, Buzón: d.Buzon, 'Buzón (seg)': d.BuzonSeg, 'Cortas <10s': d.Cortas,
        'Sin clasificar': d.SinClasificar, 'Voz efectiva': d.VozEfectiva, 'Tiempo muerto': d.TiempoMuerto,
    }))), 'Detalle por día');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(totales.map((t) => ({
        Tramo: t.Tramo, Personas: t.Personas, 'Total voz': t.TotalVoz, 'Promedio diario por persona': t.PromedioPorPersona,
        'Cumplen meta': t.PersonasCumplen, 'Bajo el mínimo': t.PersonasBajoMinimo,
        Buzón: t.Buzon, 'Buzón (tiempo)': t.BuzonVoz, 'Voz efectiva': t.VozEfectiva, 'Tiempo muerto': t.TiempoMuerto,
        '% efectividad': t.PctEfectividad ?? '',
    }))), 'Totales por tramo');
    XLSX.writeFile(wb, `TiempoVoz_${filtros.desde}_${filtros.hasta}.xlsx`);
};

/**
 * Reporte "Tiempo de voz por agente" del call center de cobranzas (dbo.sp_TiempoVozAgentes).
 */
export function ReporteCallCenter() {
    const [filtros, setFiltros] = useState(FILTROS_INICIALES);
    const [opciones, setOpciones] = useState({ extensiones: [], agentes: [], supervisores: [] });
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');
    const [ayuda, setAyuda] = useState(false);

    useEffect(() => {
        axios.get(APIURL.reporteCallCenterFiltros())
            .then((res) => setOpciones({
                extensiones: res.data?.extensiones || [],
                agentes: res.data?.agentes || [],
                supervisores: res.data?.supervisores || [],
            }))
            .catch((e) => console.error('Error cargando filtros del reporte de call center:', e));
    }, []);

    const set = (campo) => (e) => setFiltros((f) => ({
        ...f, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

    // "Hasta" siempre queda dentro del mes de "desde": si se cambia de mes, se ajusta
    const cambiarDesde = (e) => {
        const desde = e.target.value;
        setFiltros((f) => {
            if (!desde) return { ...f, desde };
            const fin = finDeMes(desde);
            const hasta = !f.hasta || !mismoMes(desde, f.hasta) || f.hasta < desde
                ? (mismoMes(desde, hoy()) && hoy() >= desde ? hoy() : fin)
                : f.hasta;
            return { ...f, desde, hasta };
        });
    };

    const generar = async () => {
        if (!filtros.desde || !filtros.hasta) { setError('Selecciona el rango de fechas.'); return; }
        if (filtros.desde > filtros.hasta) { setError('La fecha "desde" no puede ser mayor a "hasta".'); return; }
        if (!mismoMes(filtros.desde, filtros.hasta)) { setError('Las fechas deben ser del mismo mes.'); return; }
        const params = { desde: filtros.desde, hasta: filtros.hasta };
        ['tramo', 'idIngresoCobrador', 'extension', 'idSupervisor', 'estado', 'tipoDia'].forEach((k) => {
            if (filtros[k] !== '') params[k] = filtros[k];
        });
        if (filtros.soloContestadas) params.soloContestadas = 1;

        setCargando(true);
        setError('');
        try {
            const res = await axios.get(APIURL.reporteCallCenterTiempoVoz(), { params });
            setDatos({
                resumen: res.data?.resumen || [],
                detalle: res.data?.detalle || [],
                totales: res.data?.totales || [],
            });
        } catch (e) {
            const m = e?.response?.data?.message;
            setError(Array.isArray(m) ? m.join(', ') : (m || 'No se pudo generar el reporte.'));
            setDatos(null);
        } finally {
            setCargando(false);
        }
    };

    // Detalle en columnas por día (como la tabla dinámica del Excel)
    const pivot = useMemo(() => {
        if (!datos) return { dias: [], filas: [] };
        const dias = [...new Set(datos.detalle.map((d) => soloFecha(d.Fecha)))].sort();
        const porAgente = new Map();
        datos.detalle.forEach((d) => {
            const clave = `${d.Tramo}|${d.Persona}`;
            if (!porAgente.has(clave)) porAgente.set(clave, {});
            porAgente.get(clave)[soloFecha(d.Fecha)] = d;
        });
        const filas = datos.resumen.map((r) => ({ ...r, dias: porAgente.get(`${r.Tramo}|${r.Persona}`) || {} }));
        return { dias, filas };
    }, [datos]);

    const th = 'py-2.5 px-3 text-left text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap';
    const td = 'py-2 px-3 whitespace-nowrap';

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F19] py-6">
            <div className="max-w-[1600px] mx-auto px-4 space-y-4">
                {/* Encabezado */}
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-600 rounded-lg"><FaHeadset className="text-white" /></div>
                    <div>
                        <h1 className="text-lg font-bold text-slate-800 dark:text-white">Tiempo de voz por agente</h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Segundos hablados en llamadas salientes de cada extensión, contra la meta diaria del tramo.</p>
                    </div>
                    <button type="button" onClick={() => setAyuda(true)}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 dark:bg-[#161F30] dark:text-indigo-300 dark:border-indigo-700/60 dark:hover:bg-slate-800">
                        <FaCircleQuestion /> Ayuda
                    </button>
                </div>
                <AyudaReporteCallCenter abierto={ayuda} onClose={() => setAyuda(false)} />

                {/* Filtros */}
                <section className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                        <Campo label="Desde"><input type="date" className={inputCls} value={filtros.desde} onChange={cambiarDesde} /></Campo>
                        <Campo label="Hasta">
                            <input type="date" className={inputCls} value={filtros.hasta} onChange={set('hasta')}
                                min={filtros.desde || undefined} max={filtros.desde ? finDeMes(filtros.desde) : undefined} />
                        </Campo>
                        <Campo label="Tramo">
                            <select className={inputCls} value={filtros.tramo} onChange={set('tramo')}>
                                <option value="">Todos</option>
                                {TRAMOS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Agente">
                            <select className={inputCls} value={filtros.idIngresoCobrador} onChange={set('idIngresoCobrador')}>
                                <option value="">Todos</option>
                                {opciones.agentes.map((a) => <option key={a.idIngresoCobrador} value={a.idIngresoCobrador}>{a.Nombre}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Extensión">
                            <select className={inputCls} value={filtros.extension} onChange={set('extension')}>
                                <option value="">Todas</option>
                                {opciones.extensiones.map((x) => <option key={x.Extension} value={x.Extension}>{x.Extension}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Supervisor">
                            <select className={inputCls} value={filtros.idSupervisor} onChange={set('idSupervisor')}>
                                <option value="">Todos</option>
                                {opciones.supervisores.map((s) => <option key={s.idSupervisor} value={s.idSupervisor}>{s.Nombre}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Estado">
                            <select className={inputCls} value={filtros.estado} onChange={set('estado')}>
                                <option value="">Todos</option>
                                {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
                            </select>
                        </Campo>
                        <Campo label="Días">
                            <select className={inputCls} value={filtros.tipoDia} onChange={set('tipoDia')}>
                                <option value="">Todos</option>
                                <option value="0">Lunes a viernes</option>
                                <option value="6">Fin de semana</option>
                            </select>
                        </Campo>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                        <label className="inline-flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                            <input type="checkbox" checked={filtros.soloContestadas} onChange={set('soloContestadas')} className="rounded border-slate-300" />
                            Solo llamadas contestadas
                        </label>
                        <div className="ml-auto flex gap-2">
                            <button type="button" onClick={() => setFiltros(FILTROS_INICIALES)}
                                className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                                Limpiar
                            </button>
                            <button type="button" onClick={() => datos && exportarExcel(datos, filtros)} disabled={!datos?.resumen.length}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 dark:bg-[#161F30] dark:hover:bg-slate-700 dark:text-slate-200 dark:border-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50">
                                <FaFileExcel className="text-emerald-600 dark:text-emerald-400" /> Excel
                            </button>
                            <button type="button" onClick={generar} disabled={cargando}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1.5 disabled:opacity-60">
                                <FaMagnifyingGlass className="text-[11px]" /> {cargando ? 'Generando…' : 'Generar'}
                            </button>
                        </div>
                    </div>
                    {error && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
                </section>

                <p className="flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                    <FaCircleInfo className="mt-0.5 flex-shrink-0" />
                    Solo se registran las extensiones 11xx: el tiempo de quien todavía marca desde extensiones 2xx sale incompleto.
                    Si una extensión se reasignó, sus llamadas anteriores aparecen a nombre del usuario actual.
                    La meta se mide sobre el tiempo de voz total; voz efectiva y tiempo muerto son informativos.
                    &quot;Sin clasificar&quot; son contestadas que aún no se transcriben (las del día pueden estar en cola).
                </p>

                {datos && datos.resumen.length === 0 && (
                    <div className="py-10 text-center text-sm text-slate-400">No hay llamadas con los filtros seleccionados.</div>
                )}

                {datos && datos.resumen.length > 0 && (
                    <>
                        {/* Totales por tramo */}
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                            {datos.totales.map((t) => (
                                <div key={t.Tramo} className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Tramo {t.Tramo}</p>
                                    <p className="text-lg font-bold text-slate-800 dark:text-white font-mono">{t.TotalVoz}</p>
                                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                        {t.Personas} persona{t.Personas === 1 ? '' : 's'} · prom. diario {t.PromedioPorPersona}
                                    </p>
                                    <p className="text-[11px] mt-1">
                                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{t.PersonasCumplen} cumplen</span>
                                        {' · '}
                                        <span className="text-rose-600 dark:text-rose-400 font-semibold">{t.PersonasBajoMinimo} bajo mínimo</span>
                                    </p>
                                    <p className="text-[11px] mt-1 pt-1 border-t border-slate-100 dark:border-slate-800 text-slate-500 dark:text-slate-400">
                                        Efectiva <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{t.VozEfectiva}</span>
                                        {t.PctEfectividad != null && <span> ({Number(t.PctEfectividad).toFixed(1)}%)</span>}
                                        <span className="block">Buzón {t.Buzon} · muerto <span className="font-mono">{t.TiempoMuerto}</span></span>
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Resumen por agente */}
                        <section className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                            <h2 className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-white bg-slate-50 dark:bg-[#0D131F] border-b border-slate-200 dark:border-slate-800">Resumen por agente</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-100 dark:bg-[#161F30]">
                                        <tr>
                                            <th className={th}>Tramo</th><th className={th}>Persona</th><th className={th}>Ext.</th>
                                            <th className={`${th} text-right`}>Días</th><th className={`${th} text-right`}>Llamadas</th>
                                            <th className={`${th} text-right`}>Contestadas</th><th className={`${th} text-right`}>Total voz</th>
                                            <th className={`${th} text-right`}>Prom. diario</th><th className={`${th} text-right`}>Mínimo</th>
                                            <th className={`${th} text-right`}>Meta</th><th className={th}>Estado</th><th className={`${th} text-right`}>% meta</th>
                                            <th className={`${th} text-right border-l border-slate-200 dark:border-slate-700`} title="No contestó, ocupado o falló">No contestan</th>
                                            <th className={`${th} text-right`} title="Buzón de voz, operadora o grabación sin voz">Buzón</th>
                                            <th className={`${th} text-right`} title="Contestadas de menos de 10 s">Cortas</th>
                                            <th className={`${th} text-right`} title="Contestadas aún sin transcribir">Sin clasif.</th>
                                            <th className={`${th} text-right`} title="Tiempo hablado solo en conversaciones">Voz efectiva</th>
                                            <th className={`${th} text-right`} title="Timbrado de todas las llamadas + tiempo en buzón">Tiempo muerto</th>
                                            <th className={`${th} text-right`} title="Voz efectiva / tiempo de voz">% efect.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-slate-700 dark:text-slate-300">
                                        {datos.resumen.map((r) => (
                                            <tr key={`${r.Tramo}|${r.Persona}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                <td className={td}>{r.Tramo}</td>
                                                <td className={`${td} font-medium text-slate-800 dark:text-slate-200`}>{r.Persona}</td>
                                                <td className={`${td} font-mono`}>{r.Extension}</td>
                                                <td className={`${td} text-right`}>{r.DiasTrabajados}</td>
                                                <td className={`${td} text-right`}>{r.Llamadas}</td>
                                                <td className={`${td} text-right`}>{r.Contestadas}</td>
                                                <td className={`${td} text-right font-mono`}>{r.TotalVoz}</td>
                                                <td className={`${td} text-right font-mono font-semibold`}>{r.PromedioDiario}</td>
                                                <td className={`${td} text-right font-mono`}>{r.Minimo || '-'}</td>
                                                <td className={`${td} text-right font-mono`}>{r.Meta || '-'}</td>
                                                <td className={td}>
                                                    <span className={`px-2 py-0.5 rounded border text-[10px] font-semibold ${CLASE_ESTADO[r.Estado] || CLASE_ESTADO['Sin meta']}`}>{r.Estado}</span>
                                                </td>
                                                <td className={`${td} text-right font-semibold`}>{r.PctMeta != null ? `${Number(r.PctMeta).toFixed(1)}%` : '-'}</td>
                                                <td className={`${td} text-right border-l border-slate-200 dark:border-slate-700`}>{r.NoContestan}</td>
                                                <td className={`${td} text-right`}>
                                                    {r.Buzon}<span className="block text-[10px] font-mono text-slate-400">{r.BuzonVoz}</span>
                                                </td>
                                                <td className={`${td} text-right`}>
                                                    {r.Cortas}<span className="block text-[10px] font-mono text-slate-400">{r.CortasVoz}</span>
                                                </td>
                                                <td className={`${td} text-right ${r.SinClasificar ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>{r.SinClasificar}</td>
                                                <td className={`${td} text-right font-mono font-semibold text-emerald-700 dark:text-emerald-400`}>{r.VozEfectiva}</td>
                                                <td className={`${td} text-right font-mono text-rose-700 dark:text-rose-400`}>{r.TiempoMuerto}</td>
                                                <td className={`${td} text-right font-semibold`}>{r.PctEfectividad != null ? `${Number(r.PctEfectividad).toFixed(1)}%` : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        {/* Detalle por día */}
                        <section className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                            <div className="px-4 py-3 bg-slate-50 dark:bg-[#0D131F] border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-3">
                                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-white">Tiempo de voz por día</h2>
                                <span className="text-[10px] text-slate-500 dark:text-slate-400 flex gap-2">
                                    <span className="px-1.5 rounded bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">cumple meta</span>
                                    <span className="px-1.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">sobre el mínimo</span>
                                    <span className="px-1.5 rounded bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">bajo el mínimo</span>
                                </span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="text-xs">
                                    <thead className="bg-slate-100 dark:bg-[#161F30]">
                                        <tr>
                                            <th className={`${th} sticky left-0 bg-slate-100 dark:bg-[#161F30]`}>Persona</th>
                                            {pivot.dias.map((d) => <th key={d} className={`${th} text-center`}>{fechaCorta(d)}</th>)}
                                            <th className={`${th} text-right`}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {pivot.filas.map((f) => (
                                            <tr key={`${f.Tramo}|${f.Persona}`}>
                                                <td className={`${td} sticky left-0 bg-white dark:bg-[#111827] font-medium text-slate-800 dark:text-slate-200`}>
                                                    {f.Persona}
                                                    <span className="block text-[10px] text-slate-400">{f.Tramo}</span>
                                                </td>
                                                {pivot.dias.map((d) => {
                                                    const celda = f.dias[d];
                                                    return (
                                                        <td key={d} className={`${td} text-center font-mono ${claseDia(celda)}`}
                                                            title={celda ? `${celda.DiaSemana} · ${celda.Llamadas} llamadas, ${celda.Contestadas} contestadas
No contestan: ${celda.NoContestan} · Buzón: ${celda.Buzon} · Cortas: ${celda.Cortas} · Sin clasificar: ${celda.SinClasificar}
Voz efectiva: ${celda.VozEfectiva} · Tiempo muerto: ${celda.TiempoMuerto}` : ''}>
                                                            {celda ? celda.Voz : '-'}
                                                        </td>
                                                    );
                                                })}
                                                <td className={`${td} text-right font-mono font-semibold text-slate-800 dark:text-slate-200`}>{f.TotalVoz}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}
